import * as zod from 'zod';
import type { GetSuggestionsInputs } from './getSuggestions';
import {
  getScenarioImprovementsParamDefaults,
  scenarioImprovementsParamsSchemaObject,
} from '../../ScenarioImprovementParams';
import { OPTION_NO_CARGO_ALLOCATIONS } from './Suggestions';

export enum Objective {
  MinimizeCost = 'min_cost',
  /** VCP = Voyage Cargo Performance */
  MaximizeVCP = 'max_vcp',
  ShortestDistance = 'min_distance',
  ShortestBallast = 'min_ballast',
  MaximizeTCE = 'max_tce',
}

export const voyageSuggestionParamsSchema = zod.object({
  ...scenarioImprovementsParamsSchemaObject,
  orderSelection: zod.enum([
    'all',
    'only_visible',
    'only_selected',
    'exclude_selected',
  ]),
  orderTypes: zod.object({
    coaPreliminaries: zod.boolean(),
    fixedCoas: zod.boolean(),
    fixedSpots: zod.boolean(),
    inquiries: zod.boolean(),
  }),
  voyageType: zod.enum([
    'new_voyage',
    'new_or_existing_voyage',
    'existing_voyage',
  ]),
  vesselType: zod.enum(['shown', 'all', 'selected']),
  objective: zod.nativeEnum(Objective),
  numOrders: zod.number().min(1),
  computationBudget: zod.number().min(0.1),
  /** How much total delay allowed with no penalty */
  toleratedDelayH: zod.number().min(0),
  /** How much total delay allowed before combination is discarded */
  maxAllowedTotalDelayH: zod.number().min(0),
  /** How much total idle allowed with no penalty */
  toleratedIdleH: zod.number().min(0),

  firstAllowedTime: zod.number().default(() => Date.now()),
  lastAllowedTime: zod.number().nullable().default(null),

  maxBallastDistanceNm: zod.number().default(1000),
});

export type VoyageSuggestionParams = zod.infer<
  typeof voyageSuggestionParamsSchema
>;

export const getVoyageSuggestionParamDefaults: () => GetSuggestionsInputs =
  () => ({
    ...getScenarioImprovementsParamDefaults(),
    orderSelection: 'all',
    objective: Objective.MaximizeVCP,
    orderTypes: {
      coaPreliminaries: true,
      fixedCoas: true,
      fixedSpots: true,
      inquiries: true,
    },
    voyageType: 'new_voyage',
    vesselType: 'shown',
    numOrders: 1,
    computationBudget: 1,
    toleratedDelayH: 3 * 24,
    maxAllowedTotalDelayH: 7 * 24,
    toleratedIdleH: 14 * 24,
    maxBallastDistanceNm: 1000,

    firstAllowedTime: Date.now(),
    lastAllowedTime: null,

    addToShipmentId: null,
    insertAfter: null,
    insertBefore: null,
    delayUntilLD: false,
    allocationOptions: OPTION_NO_CARGO_ALLOCATIONS,
    violationBasis: 'eta',
    specialRequirements: new Map(),
  });
